import { Copy, Ban, CheckCircle, Trash2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useKeys, useDeleteKey, useBlockKey } from '@/api/hooks/useKeys';

export function KeysPage() {
  const { data, isLoading } = useKeys();
  const deleteKey = useDeleteKey();
  const blockKey = useBlockKey();

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Virtual Keys</h1>
        <Link to="/keys/new">
          <Button>
            <Plus size={16} />
            <span>Generate Key</span>
          </Button>
        </Link>
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <table>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <Th>Alias</Th>
              <Th>User</Th>
              <Th>Team</Th>
              <Th align="right">Spend / Budget</Th>
              <Th>Status</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {data?.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: 32,
                    textAlign: 'center',
                    fontSize: 13,
                    color: '#64748b',
                  }}
                >
                  No keys yet
                </td>
              </tr>
            ) : (
              data?.map((key) => {
                const display = key.key_alias ?? `sk-...${key.key.slice(-8)}`;
                return (
                  <tr key={key.key} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <Td>
                      <Link
                        to={`/keys/${encodeURIComponent(key.key)}`}
                        style={{ color: '#0f172a', fontWeight: 500, textDecoration: 'none' }}
                      >
                        {display}
                      </Link>
                    </Td>
                    <Td>
                      <span style={{ color: '#64748b' }}>{key.user_id ?? '-'}</span>
                    </Td>
                    <Td>
                      <span style={{ color: '#64748b' }}>{key.team_id ?? '-'}</span>
                    </Td>
                    <Td align="right">
                      ${(key.spend ?? 0).toFixed(2)} /{' '}
                      {key.max_budget != null ? `$${key.max_budget.toFixed(2)}` : '∞'}
                    </Td>
                    <Td>
                      {key.blocked ? (
                        <span style={{ color: '#dc2626' }}>Blocked</span>
                      ) : (
                        <span style={{ color: '#16a34a' }}>Active</span>
                      )}
                    </Td>
                    <Td align="right">
                      <div
                        style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigator.clipboard.writeText(key.key)}
                          title="Copy full key"
                        >
                          <Copy size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            blockKey.mutate({ key: key.key, blocked: !key.blocked })
                          }
                          title={key.blocked ? 'Unblock' : 'Block'}
                        >
                          {key.blocked ? (
                            <CheckCircle size={16} />
                          ) : (
                            <Ban size={16} />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm(`Delete key "${display}"?`)) {
                              deleteKey.mutate(key.key);
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} color="#dc2626" />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '12px 16px',
        textAlign: align ?? 'left',
        fontSize: 13,
        fontWeight: 500,
        color: '#475569',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '12px 16px',
        textAlign: align ?? 'left',
        fontSize: 13,
      }}
    >
      {children}
    </td>
  );
}
